import { toast, updateGlobalProperties } from '@remote/utils/index'
import { captureException } from '@sentry/vue'
import { paramsToObject } from '@shared/utils'
/**
 * 定义一个接口：FactoryRequestFunction，用于使用厂商的接口，调用套件端接口。
 * 后续接入新厂商的时候，只需要根据这个接口实现请求函数即可。
 */
interface FactoryRequestFunction {
  (payload: API_DEVICE.FactoryRequestPayload): Promise<unknown>
}

interface DeviceAuthParams {
  //privilege=PLATFORM_XCLOUD&runner_space=&space=device_id%23d9ebf7685d30b57c8f64449ca694928e&user_code=AWMXCloZAXlx-qLu0g
  client_id?: string
  error_message?: string
  noActionBar?: 'true' | 'false'
  noStatusBar?: 'true' | 'false'
  platform?: 'huawei'
  plm?: 'hw'
  privilege?: string
  runner_space?: string
  space?: string
  user_code?: string
}

/**
 * 返回一个函数：使用华为和套件通信的机制，对套件发起请求。
 * 后续接入厂商，可以参考这个实现请求函数，复用认证流程。
 */
const useHuaweiRequest = (): FactoryRequestFunction => {
  const generateRequestID = () => {
    const timestamp = Date.now() // 当前时间戳
    const random = Math.floor(Math.random() * 1000000) // 一个6位随机数
    return `FUNC${timestamp}${random}`
  }
  if (window.hdc) { // 华为Android
    const requestFunction = (payload: API_DEVICE.FactoryRequestPayload): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        try {
          const requestID = generateRequestID()
          window[requestID] = (resp: string) => {
            try {
              const response: { code: number; data?: { response: unknown; pkgName: string; function: string } } = JSON.parse(resp)
              if (response.code === 0 && response?.data?.response) {
                resolve(response.data.response)
                return
              }
              reject(new Error('连接设备失败'))
            } catch (error) {
              reject(error)
            }
          }
          const stringifiedPayload = JSON.stringify(payload)
          console.log('payload', payload)
          window.hdc.getServiceData('com.xunlei.download', stringifiedPayload, requestID)
        } catch (error) {
          reject(error)
        }
      })
    }
    return requestFunction
  } else if (window?.webkit?.messageHandlers?.hdc) {
    // 华为 iOS
    const requestFunction = (payload: API_DEVICE.FactoryRequestPayload): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        try {
          const requestID = generateRequestID()
          window[requestID] = (resp: { result: { code: number; data?: { response: unknown; pkgName: string; function: string } }}) => {
            try {
              const response = resp.result
              if (response.code === 0 && response?.data?.response) {
                resolve(response.data.response)
                return
              }
              reject(new Error('连接设备失败'))
            } catch (error) {
              reject(error)
            }
          }
          // const stringifiedPayload = JSON.stringify(payload)
          console.log('payload', payload)
          window.webkit.messageHandlers.hdc.postMessage({
            method: 'getServiceData',
            callback: requestID,
            id: new Date().getTime(),
            data: {
              function: 'com.xunlei.download',
              body: payload,
            },
          })
        } catch (error) {
          reject(error)
        }
      })
    }
    return requestFunction
  }
  throw new Error('不在华为环境中: 没有找到hdc对象')
}

/**
 * 柯里化获取目前设备信息（绑定状态）
 */
export const curryGetDeviceStatus = (requestFunc: FactoryRequestFunction) => {
  return () => requestFunc({
    method: 'GET',
    path: '/device/status',
  }) as Promise<API_DEVICE.DeviceInfo>
}

/**
 * 柯里化退出设备登录态
 */
const currySignOut = (requestFunc: FactoryRequestFunction) => {
  return (type?: 'relogin') => requestFunc({
    method: 'POST',
    path: '/auth/signout',
    type,
  }) as Promise<{}>
}

/**
 * 柯里化获取设备绑定链接
 */
const curryGetAuthLink = (requestFunc: FactoryRequestFunction) => {
  return () => requestFunc({
    method: 'GET',
    path: '/auth/login',
  }) as Promise<{ url?: string }>
}

/**
 * 柯里化获取入口链接
 */
const curryGetEntryLink = (requestFunc: FactoryRequestFunction) => {
  return () => requestFunc({
    method: 'GET',
    path: '/device/control',
  }) as Promise<{ url?: string }>
}


/**
 * 厂商认证流程
 */
export const useAuth = (factory = 'huawei') => {
  const mapFactoryToRequestComposable: Record<string, () => FactoryRequestFunction> = {
    huawei: useHuaweiRequest,
  }

  const updateAuthStatus = async () => {
    if (mapFactoryToRequestComposable[factory]) {
      const curRequestFunction = mapFactoryToRequestComposable[factory]()
      const linkResp = await curryGetEntryLink(curRequestFunction)()
      console.log('获取控制链接并更新global上的全局属性：', linkResp)
      if (linkResp.url) {
        const query = paramsToObject((new URL(linkResp.url).searchParams))
        updateGlobalProperties(query)
        if (query?.error_message) {
          console.error('auth: error_message:', query.error_message)
          toast(query.error_message)
        }
      }
    }
  }
  /**
   * 认证，绑定迅雷账号和设备 
   */
  const auth = async () => {
    try {
      if (mapFactoryToRequestComposable[factory]) {
        const curRequestFunction = mapFactoryToRequestComposable[factory]()
        // window.signout = async () => {
        //   await currySignOut(curRequestFunction)()
        // }
        const status = await curryGetDeviceStatus(curRequestFunction)()
        console.log('获取设备登录状态: ', status)
        if (status?.user?.id) {
          const linkResp = await curryGetEntryLink(curRequestFunction)()
          console.log('设备已登录，获取控制链接：', linkResp)
          if (linkResp.url) {
            const query = paramsToObject((new URL(linkResp.url).searchParams))
            updateGlobalProperties(query)
            if (query.error_message) {
              console.error('auth: error_message:', query.error_message)
              toast(query.error_message)
            }
          }
          return [true, status.user] as const // 未登录情况，这里ID应该为''，而不是在其他前端项目更常见的‘0’。因此这个判断是否登录应该是安全的。
        } 
        const linkResp = await curryGetAuthLink(curRequestFunction)()
        console.log('设备未登录，获取登录链接：', linkResp)
        if (linkResp.url) {
          const authParams = paramsToObject((new URL(linkResp.url).searchParams)) as DeviceAuthParams
          updateGlobalProperties(authParams)
          return [false, authParams] as const
        }
        throw new Error('Auth: 没有获取到认证链接')
      }
      throw new Error('Auth: 没有匹配的厂商请求函数')
    } catch (error) {
      toast(`${error.message}；请退出后重试`)
      captureException(error)
      console.error(error.message)
      throw new Error('auth failed.')
    }
  }

  /**
   * 解绑设备并重新登录 
   */
  const signOut = async (type?: 'relogin') => {
    if (mapFactoryToRequestComposable[factory]) {
      const curRequestFunction = mapFactoryToRequestComposable[factory]()
      await currySignOut(curRequestFunction)(type)
    }
    throw new Error('ReLogin: 没有匹配的厂商请求函数.')
  }

  return {
    auth,
    signOut,
    updateAuthStatus,
  }
}
